import React, { useEffect, useState } from "react";
import { FormControl, TextField, Checkbox, Popper } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FieldError } from "react-hook-form/dist/types";
import { withFormError, withFormRef } from "../form-integration";
import useAutoCompleteStyles from "./autocomplete.styles";
import ErrorMessage from "../error/error-message";
import { PopoverTooltip } from "../tooltip/tooltip";
import HelpIcon from '@material-ui/icons/Help';

export interface AutoCompleteProps {
  helpText?: string;
  optional?: boolean;
  value?: any | any[];
  id?: string;
  name: string;
  fieldDescription?: string;
  options?: any;
  ref?: React.Ref<HTMLElement>;
  errorMessage?: string;
  hasError?: boolean;
  helpLabel?: string;
  disabled?: boolean;
  onChange?: Function;
  error?: FieldError;
  setValue?: any;
  innerRef?: any;
  multiple?: boolean;
  unregister?: any;
  label?: string;
  reqValidationMessage: string;
  required?: boolean;
}

const AutoComplete: React.FC<AutoCompleteProps> = ({
  options,
  value,
  id,
  name,
  fieldDescription,
  errorMessage,
  hasError,
  disabled,
  onChange = (): void => {},
  setValue = (): void => {},
  multiple = false,
  innerRef,
  unregister,
  label,
  required,
}) => {


  /**
   * Function that returns a default value for the autocomplete
   * displayed when the component loads for the first time.
   * This function checks if the value is part of options.
   * @returns default value for the autocomplete
   */
  const getDefaultValue = () => {
    if (!value) {
      return multiple ? [] : "";
    }

    if (multiple) {
      // For Multiselect, if it is a string, we need to split it
      // Check if all the values are in the options
      const vals = typeof value === "string" ? value.split("|") : value;
      const allValsInOptions = vals.every((val: any) =>
        options.find((option: any) => option === val)
      );
      return allValsInOptions ? vals : [];
    }

    // Check if value is in options
    const valInOptions = options.find((option: any) => option === value);
    return valInOptions ? value : "";
  };

  const concat = (data) => {
    return Array.isArray(data) ? data.join("|") : data;
  };

  useEffect(() => {
    if (value) {
      // Set shouldValidate flag so that the form's isValid
      // field is updated
      setValue(name, value, { shouldValidate: true });
    } else {
      // Don't set shouldValidate flag when value is not present
      setValue(name, value);
    }
  }, []);

  const [selectedValue, setSelectedValue] = useState(concat(value));

  const classes = useAutoCompleteStyles();

  const handleChange = (event, value) => {
    setSelectedValue(Array.isArray(value) ? value.join("|") : value);
    setValue(name, Array.isArray(value) ? value.join("|") : value, {
      shouldValidate: true,
    });
    if (onChange) {
      onChange(event, value);
    }
  };

  /**
   * Event handler for onBlur event
   * For multi select, when user clicks outside of the dropdown,
   * the selected values are rendered as div elements and the input value is being set as empty
   * this causes the control to be invalid. To handle this scenario, selected value is checked and
   * validation is triggered
   * @param e blur event
   */
  const handleOnBlur = (e) => {
    if (!selectedValue || selectedValue.length === 0) {
      setValue(name, selectedValue, { shouldValidate: true });
    } else {
      setValue(
        name,
        Array.isArray(selectedValue) ? selectedValue.join("|") : selectedValue
      );
    }
  };

  return (
    <FormControl fullWidth>
      <Autocomplete
        id={id}
        className={classes.root}
        {...(multiple && {
          multiple: multiple,
          disableCloseOnSelect: true,
          renderOption: (option, { selected }) => (
            <>
              <Checkbox
                className={classes.checkbox}
                data-testid="checkbox"
                style={{ marginRight: 8 }}
                checked={selected}
                color="primary"
              />
              {option}
            </>
          ),
        })}
        options={options}
        getOptionLabel={(option: string) => option}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            fullWidth
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              autoComplete: "off",
              id: id,
              endAdornment: (
                <React.Fragment>
                  {fieldDescription && <div className={classes.helpTooltipAdornmentWrapper}><PopoverTooltip messageDescription={fieldDescription}><HelpIcon /></PopoverTooltip></div>}
                  <div className={fieldDescription ? classes.helpTooltipAdornment : null}>
                    {params.InputProps.endAdornment}
                  </div>
                </React.Fragment>
              ),
            }}
            {...(innerRef && { inputRef: innerRef })}
            error={hasError}
            label={
              <span className={hasError ? classes.error : ""}>
                {label}
                {required && (
                  <span className={classes.requiredAsterisk}>*</span>
                )}
              </span>
            }
          />
        )}
        onChange={handleChange}
        onBlur={handleOnBlur}
        disabled={disabled}
        defaultValue={getDefaultValue()}
      />
      {hasError ? <ErrorMessage message={errorMessage} /> : null}
    </FormControl>
  );
};

export default withFormRef(
  withFormError(AutoComplete)
) as React.FC<AutoCompleteProps>;